import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { Shield, Info, Settings, Globe } from 'lucide-react';

const CookiePolicy = () => {
  const { themeColors } = useTheme();

  const containerStyle = {
    minHeight: 'calc(100vh - 120px)',
    backgroundColor: themeColors.background,
    padding: '40px 20px'
  };

  const contentStyle = {
    maxWidth: '900px',
    margin: '0 auto'
  };

  const headerStyle = {
    textAlign: 'center',
    marginBottom: '60px'
  };

  const titleStyle = {
    color: themeColors.text,
    fontSize: '48px',
    fontWeight: 'bold',
    marginBottom: '20px'
  };

  const subtitleStyle = {
    color: themeColors.textSecondary,
    fontSize: '18px',
    lineHeight: '1.6',
    maxWidth: '700px',
    margin: '0 auto'
  };

  const lastUpdatedStyle = {
    color: themeColors.textSecondary,
    fontSize: '14px',
    fontStyle: 'italic',
    marginTop: '12px'
  };

  const sectionStyle = {
    marginBottom: '48px'
  };

  const sectionTitleStyle = {
    color: themeColors.text,
    fontSize: '24px',
    fontWeight: 'bold',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  };

  const cardStyle = {
    backgroundColor: themeColors.cardBackground,
    borderRadius: '16px',
    padding: '32px',
    marginBottom: '24px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
  };

  const paragraphStyle = {
    color: themeColors.textSecondary,
    fontSize: '16px',
    lineHeight: '1.8',
    marginBottom: '16px'
  };

  const listStyle = {
    color: themeColors.textSecondary,
    fontSize: '16px',
    lineHeight: '2',
    paddingLeft: '20px',
    marginBottom: '16px'
  };

  const cookieTypeStyle = {
    backgroundColor: themeColors.background,
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '16px',
    border: `1px solid ${themeColors.border}`
  };

  const cookieTypeTitleStyle = {
    color: themeColors.text,
    fontSize: '18px',
    fontWeight: 'bold',
    marginBottom: '12px'
  };

  const badgeStyle = {
    display: 'inline-block',
    backgroundColor: themeColors.primary + '20',
    color: themeColors.primary,
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
    marginLeft: '8px'
  };

  const linkStyle = {
    color: themeColors.primary,
    textDecoration: 'none',
    fontWeight: '500'
  };

  return (
    <div style={containerStyle}>
      <div style={contentStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <h1 style={titleStyle}>Cookie Policy</h1>
          <p style={subtitleStyle}>
            This policy explains how BabyResell uses cookies and similar technologies.
          </p>
          <p style={lastUpdatedStyle}>Last updated: March 2026</p>
        </div>

        {/* What Are Cookies */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>
            <Info size={28} color={themeColors.primary} />
            What Are Cookies?
          </h2>
          <div style={cardStyle}>
            <p style={paragraphStyle}>
              Cookies are small text files stored on your device when you visit a website. They help the site remember your preferences, understand how you use it, and improve your experience. Cookies are widely used across the internet and are essential for many website features to work properly.
            </p>
          </div>
        </div>

        {/* How We Use Cookies */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>
            <Settings size={28} color={themeColors.primary} />
            How We Use Cookies
          </h2>
          <p style={{ ...paragraphStyle, marginBottom: '24px' }}>
            We use the following types of cookies:
          </p>

          {/* Essential Cookies */}
          <div style={cookieTypeStyle}>
            <div style={cookieTypeTitleStyle}>
              Essential Cookies
              <span style={badgeStyle}>Always Active</span>
            </div>
            <p style={paragraphStyle}>
              These cookies are necessary for the website to function and cannot be disabled. They include cookies that manage your login session, shopping cart, and security features.
            </p>
            <p style={{ ...paragraphStyle, fontWeight: '600', marginBottom: '8px', fontSize: '15px' }}>
              Examples:
            </p>
            <ul style={listStyle}>
              <li>Authentication tokens (keeping you logged in)</li>
              <li>Session identifiers</li>
              <li>Security cookies (CSRF protection)</li>
              <li>Cookie consent preferences</li>
            </ul>
          </div>

          {/* Functional Cookies */}
          <div style={cookieTypeStyle}>
            <div style={cookieTypeTitleStyle}>
              Functional Cookies
            </div>
            <p style={paragraphStyle}>
              These cookies enable enhanced functionality and personalization, such as remembering your theme preference, language settings, and recently viewed items.
            </p>
            <p style={{ ...paragraphStyle, fontWeight: '600', marginBottom: '8px', fontSize: '15px' }}>
              Examples:
            </p>
            <ul style={listStyle}>
              <li>Theme preference (light/dark/seasonal)</li>
              <li>Language and region settings</li>
              <li>Recently viewed items</li>
              <li>Search preferences and filters</li>
            </ul>
          </div>

          {/* Analytics Cookies */}
          <div style={cookieTypeStyle}>
            <div style={cookieTypeTitleStyle}>
              Analytics Cookies
            </div>
            <p style={paragraphStyle}>
              These cookies help us understand how visitors use our website so we can improve the experience. All data is aggregated and anonymous.
            </p>
            <p style={{ ...paragraphStyle, fontWeight: '600', marginBottom: '8px', fontSize: '15px' }}>
              Examples:
            </p>
            <ul style={listStyle}>
              <li>Page view tracking</li>
              <li>Feature usage statistics</li>
              <li>Error and performance monitoring</li>
              <li>Traffic source analysis</li>
            </ul>
          </div>

          {/* Marketing Cookies */}
          <div style={cookieTypeStyle}>
            <div style={cookieTypeTitleStyle}>
              Marketing Cookies
              <span style={{ ...badgeStyle, backgroundColor: themeColors.textSecondary + '20', color: themeColors.textSecondary }}>
                Not Currently Used
              </span>
            </div>
            <p style={paragraphStyle}>
              We do not currently use marketing or advertising cookies. If this changes in the future, we will update this policy and request your consent.
            </p>
          </div>
        </div>

        {/* Third-Party Cookies */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>
            <Globe size={28} color={themeColors.primary} />
            Third-Party Cookies
          </h2>
          <div style={cardStyle}>
            <p style={paragraphStyle}>
              Some cookies on our site are set by third-party services we use:
            </p>
            <ul style={listStyle}>
              <li><strong>Stripe</strong> — Payment processing (essential for secure checkout)</li>
              <li><strong>Cloudinary</strong> — Image delivery optimization</li>
              <li><strong>Google Analytics</strong> — Anonymous usage statistics (if implemented)</li>
            </ul>
            <p style={paragraphStyle}>
              These third parties have their own privacy and cookie policies. We encourage you to review them.
            </p>
          </div>
        </div>

        {/* Managing Cookie Preferences */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>
            <Settings size={28} color={themeColors.primary} />
            Managing Your Cookie Preferences
          </h2>
          <div style={cardStyle}>
            <p style={paragraphStyle}>
              You can control cookies through your browser settings:
            </p>
            <ul style={listStyle}>
              <li><strong>Chrome:</strong> Settings → Privacy and Security → Cookies</li>
              <li><strong>Firefox:</strong> Settings → Privacy & Security → Cookies</li>
              <li><strong>Safari:</strong> Preferences → Privacy → Cookies</li>
              <li><strong>Edge:</strong> Settings → Privacy → Cookies</li>
            </ul>
            <p style={{ ...paragraphStyle, backgroundColor: themeColors.warning + '15', padding: '16px', borderRadius: '8px', borderLeft: `4px solid ${themeColors.warning}` }}>
              <strong>Please note:</strong> Disabling essential cookies may prevent some features of BabyResell from working properly, such as logging in or completing purchases.
            </p>
          </div>
        </div>

        {/* Changes to This Policy */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>
            <Info size={28} color={themeColors.primary} />
            Changes to This Policy
          </h2>
          <div style={cardStyle}>
            <p style={paragraphStyle}>
              We may update this Cookie Policy from time to time. When we make changes, we'll update the "Last updated" date at the top of this page. Continued use of BabyResell after changes are posted constitutes your acceptance of the updated policy.
            </p>
          </div>
        </div>

        {/* Contact Us */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>
            <Shield size={28} color={themeColors.primary} />
            Contact Us
          </h2>
          <div style={cardStyle}>
            <p style={paragraphStyle}>
              If you have questions about our use of cookies, please contact us at:
            </p>
            <ul style={{ ...listStyle, listStyle: 'none', paddingLeft: 0 }}>
              <li>
                Email:{' '}
                <a href="mailto:privacy@interverse.app" style={linkStyle}>
                  privacy@interverse.app
                </a>
              </li>
              <li>
                Or visit our{' '}
                <a href="/contact" style={linkStyle}>
                  Contact Support
                </a>{' '}
                page
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicy;
